import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import EventCard from "../components/EventCard";
import FeaturedCarousel from "../components/FeaturedCarousel";
import { ArrowRight, MusicNotes, SoccerBall, Confetti, MaskHappy, GraduationCap, Microphone, ShieldCheck, Lightning, ChartLine, Flame, Wallet } from "@phosphor-icons/react";

const FLARE_POSTER = "https://customer-assets.emergentagent.com/job_tickethub-67/artifacts/6pov8f0n_image.png";

const CATS = [
  { key: "concerts", label: "Concerts", Icon: MusicNotes, color: "#4F46E5" },
  { key: "sports", label: "Sports", Icon: SoccerBall, color: "#0A0A0A" },
  { key: "festivals", label: "Festivals", Icon: Confetti, color: "#9333EA" },
  { key: "theater", label: "Theater", Icon: MaskHappy, color: "#0A0A0A" },
  { key: "university", label: "University", Icon: GraduationCap, color: "#4F46E5" },
  { key: "conferences", label: "Conferences", Icon: Microphone, color: "#9333EA" },
];

const TICKER = ["FLARE FEST 2026", "COKE STUDIO BANGLA", "BPL FINAL 2026", "COLDPLAY WORLD TOUR", "DHAKA FILM FEST", "TECH SUMMIT ASIA", "JAMES LIVE", "NEON NIGHTS", "STARTUP BANGLADESH"];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [flare, setFlare] = useState(null);

  useEffect(() => {
    api.get("/events/featured").then(({ data }) => {
      setFeatured(data);
      const f = data.find((e) => e.title.toLowerCase().includes("flare fest"));
      if (f) setFlare(f);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO – modern layout with elevated landscape image */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8F9FA] to-white dark:from-[#0B0F1A] dark:to-[#111827]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center lg:items-start">
            <div className="lg:col-span-6 xl:col-span-5 order-2 lg:order-1">
              <span className="label-eyebrow text-[#4F46E5] fade-in-up">Bangladesh's #1 Ticket Marketplace</span>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.88] tracking-tighter mt-4 fade-in-up">
                Get tickets to{" "}
                <span className="brand-gradient">the moments</span>{" "}
                you'll never forget.
              </h1>
              <p className="mt-6 text-base lg:text-lg text-neutral-500 dark:text-neutral-400 max-w-lg fade-in-up">
                Concerts. Sports. Festivals. Theater. University. Conferences. Verified, instant, fraud-free.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 fade-in-up">
                <Link to="/events" className="btn-red inline-flex items-center gap-2" data-testid="hero-browse-btn">
                  Browse Events <ArrowRight size={18} weight="bold" />
                </Link>
                <Link to="/organizer" className="btn-outline inline-flex items-center gap-2" data-testid="hero-sell-btn">
                  Sell with us
                </Link>
              </div>
<div className="mt-10 flex flex-wrap gap-8 text-sm text-neutral-400 fade-in-up">
                  <div><div className="font-display text-2xl text-neutral-900 dark:text-neutral-100">2.4M+</div>tickets sold</div>
                  <div><div className="font-display text-2xl text-neutral-900 dark:text-neutral-100">540+</div>events / mo</div>
                  <div><div className="font-display text-2xl text-neutral-900 dark:text-neutral-100">100%</div>verified</div>
              </div>
            </div>

            <div className="lg:col-span-6 xl:col-span-7 order-1 lg:order-2 lg:pt-1">
              <div
                className="hero-image-card relative rounded-2xl lg:rounded-3xl overflow-hidden aspect-video"
                style={{
                  boxShadow: "0 25px 50px -12px rgba(79,70,229,0.15), 0 12px 24px -8px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03)",
                  transform: "translateY(0)",
                  transition: "transform 0.5s cubic-bezier(.4,0,.2,1), box-shadow 0.5s cubic-bezier(.4,0,.2,1)",
                }}
              >
                <img
                  src={FLARE_POSTER}
                  alt="Flare Fest 2026 – Featured Event"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4 lg:top-6 lg:left-6 bg-white/90 backdrop-blur-sm text-black px-3 py-1 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 rounded-full shadow-sm">
                  <Flame size={12} weight="fill" className="text-[#4F46E5]" /> Hot Right Now
                </div>
                {flare && (
                  <Link
                    to={`/events/${flare.id}`}
                    className="absolute bottom-4 left-4 right-4 lg:bottom-6 lg:left-6 lg:right-6 bg-white/95 backdrop-blur text-black p-4 lg:p-5 rounded-xl hover:bg-[#4F46E5] hover:text-white transition-all duration-300 group"
                    data-testid="hero-poster-cta"
                  >
                    <p className="text-[10px] uppercase tracking-widest font-bold opacity-60 group-hover:opacity-80">Featured Event</p>
                    <p className="font-display text-xl lg:text-2xl mt-1">{flare.title}</p>
                    <div className="flex justify-between items-end mt-2">
                      <p className="text-xs opacity-70">{flare.venue}, {flare.city}</p>
                      <span className="text-xs font-bold uppercase tracking-widest group-hover:opacity-90">Get Tickets →</span>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Subtle fade overlap into next section */}
        <div className="h-20 lg:h-28 bg-gradient-to-b from-transparent to-white dark:to-[#111827]" />
      </section>

      {/* MARQUEE */}
      <section className="border-y border-black bg-brand-gradient text-white py-4 overflow-hidden">
        <div className="marquee font-display text-2xl tracking-tight">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-12">
              {t} <span className="text-white/60">●</span>
            </span>
          ))}
        </div>
      </section>

      {/* FEATURED EVENTS CAROUSEL */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20" data-testid="featured-carousel-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-eyebrow">01 / Featured</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tighter dark:text-white">Selling fast.</h2>
            </div>
            <Link to="/events" className="text-sm font-bold uppercase tracking-widest hover:text-[#4F46E5]">View all →</Link>
          </div>
          <FeaturedCarousel events={featured} />
        </section>
      )}

      {/* DEDICATED PROMO BANNER */}
      {flare && (
        <section className="bg-[#0A0A0A] text-white" data-testid="promo-banner">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-4 lg:col-span-3">
              <Link to={`/events/${flare.id}`} className="block aspect-square overflow-hidden rounded-2xl border border-white/10 shadow-xl">
                <img src={FLARE_POSTER} alt="Flare Fest" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </Link>
            </div>
            <div className="md:col-span-8 lg:col-span-9">
              <span className="label-eyebrow text-[#4F46E5]">Pinned Event</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl mt-2 tracking-tighter leading-none">
                A festival. <br />
                A movement. <br />
                <span className="brand-gradient">A moment.</span>
              </h2>
              <p className="mt-6 text-neutral-400 max-w-xl">
                Three nights. One stage. The biggest youth-culture festival of 2026. Limited Early Bird tickets at ৳1,200.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/events/${flare.id}`} className="btn-red inline-flex items-center gap-2" data-testid="promo-tickets-btn">
                  Get Tickets <ArrowRight size={18} weight="bold" />
                </Link>
                <Link to={`/events/${flare.id}`} className="btn-outline border-white text-white hover:bg-white hover:text-black">
                  View lineup
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-8 text-sm text-neutral-500">
                <div><span className="text-white font-bold">21–23 Nov 2026</span><br/>Three nights</div>
                <div><span className="text-white font-bold">{flare.venue}</span><br/>{flare.city}</div>
                <div><span className="text-white font-bold">From ৳1,200</span><br/>Early Bird</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="label-eyebrow">02 / Browse</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tighter dark:text-white">Find your scene.</h2>
          </div>
          <Link to="/events" className="hidden md:inline text-sm font-bold uppercase tracking-widest hover:text-[#4F46E5]">All categories →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="categories-grid">
          {CATS.map(({ key, label, Icon, color }) => (
            <Link
              key={key}
              to={`/events?category=${key}`}
              className="grid-card aspect-square flex flex-col items-center justify-center gap-3 group"
              data-testid={`category-${key}`}
            >
              <Icon size={42} weight="duotone" style={{ color }} className="transition-transform group-hover:scale-110" />
              <span className="font-display text-xl dark:text-neutral-100">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED GRID (below carousel) */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-eyebrow">03 / Trending</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tighter dark:text-white">Don't miss out.</h2>
            </div>
            <Link to="/events" className="text-sm font-bold uppercase tracking-widest hover:text-[#4F46E5]">View all →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-grid">
            {featured.slice(0, 6).map((e) => (
              <EventCard key={e.id} event={e} testId={`featured-${e.id}`} />
            ))}
          </div>
        </section>
      )}

      {/* TRUST */}
      <section className="bg-[#F8F9FA] dark:bg-[#111827] border-y border-neutral-200 dark:border-neutral-700 py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="stat-card">
            <ShieldCheck size={36} weight="duotone" className="text-[#4F46E5]" />
            <h3 className="font-display text-2xl mt-4 dark:text-white">100% Verified Tickets</h3>
            <p className="mt-2 text-neutral-600 dark:text-neutral-400">Every ticket is QR-verified. Fraud protection is built into every purchase.</p>
          </div>
<div className="stat-card">
          <Lightning size={36} weight="duotone" className="text-[#4F46E5]" />
          <h3 className="font-display text-2xl mt-4 dark:text-white">Instant Delivery</h3>
          <p className="mt-2 text-neutral-600 dark:text-neutral-400">Tickets in your wallet seconds after checkout. Email and SMS confirmations included.</p>
        </div>
        <div className="stat-card">
          <Wallet size={36} weight="duotone" className="text-[#4F46E5]" />
          <h3 className="font-display text-2xl mt-4 dark:text-white">Secure Payments</h3>
          <p className="mt-2 text-neutral-600 dark:text-neutral-400">Your transactions are protected with bank-grade encryption and multiple payment options.</p>
        </div>

        </div>
      </section>
    </div>
  );
}
